import { Controller } from "@hotwired/stimulus"
import { autoUpdate, computePosition, flip, offset, shift } from "@floating-ui/dom"
import Plyr from "plyr"
import Hls from "hls.js"

// Deepest audio compaction stage: skip + download folded, volume folded, seek dropped.
const MAX_COMPACT_LEVEL = 3

// How far a secondary stream may drift from the presenter (master clock) before
// it is hard-snapped back into sync on the next timeupdate.
const STREAM_SYNC_DRIFT_SECONDS = 0.3

// The layout labels shown in the "View" settings menu, keyed by layout id.
const STREAM_LAYOUT_LABELS = {
  mosaic: "Side by side",
  presenter: "Presenter only",
  slides: "Slides only",
  pip: "Picture in picture"
}

// Bounds for the user-controlled side-by-side split (percent of the left pane),
// so neither stream can be collapsed to nothing, plus the keyboard nudge step.
const MOSAIC_SPLIT_MIN = 15
const MOSAIC_SPLIT_MAX = 85
const MOSAIC_SPLIT_STEP = 4

export default class extends Controller {
  static targets = [
    "media",
    "mosaic",
    "stream",
    "resizer",
    "viewMenuButton",
    "viewSubmenu",
    "viewValue",
    "viewOptions",
    "loader",
    "error",
    "errorText",
    "playButton",
    "playIcon",
    "pauseIcon",
    "currentTime",
    "duration",
    "seekRange",
    "volumeContainer",
    "volumeButton",
    "volumeIcon",
    "volumePanel",
    "volumeRange",
    "settingsContainer",
    "settingsButton",
    "settingsMenu",
    "settingsRoot",
    "qualityMenuButton",
    "qualitySubmenu",
    "qualityOptions",
    "qualityValue",
    "speedMenuButton",
    "speedSubmenu",
    "speedOptions",
    "speedValue",
    "captionsButton",
    "fullscreenButton",
    "fullscreenIcon"
  ]

  static values = {
    mediaKind:      { type: String,  default: "video" },
    type:           { type: String,  default: "file" },
    src:            { type: String,  default: "" },
    poster:         { type: String,  default: "" },
    qualities:      { type: Array,   default: [] },
    downloadUrl:    { type: String,  default: "" },
    multiStream:             { type: Boolean, default: false },
    useCredentials:          { type: Boolean, default: false },
    videoControlsHideDelay:  { type: Number,  default: 2800 },
    // How long to wait for the media to become playable before showing a clear
    // "couldn't load" state with a retry, instead of spinning forever (e.g. HLS
    // not packaged yet, or a stalled raw-file fallback).
    loadTimeout:             { type: Number,  default: 15000 },
    debug:                   { type: Boolean, default: false }
  }

  connect() {
    if (!this.hasMediaTarget) return

    this.isVideo = this.mediaKindValue === "video"
    this.isHlsSource = this.typeValue === "hls" && this.srcValue.length > 0
    this.isMultiStream = this.multiStreamValue && this.mediaTargets.length > 1
    this.secondaryStreams = []
    this.secondaryHlsInstances = []
    this.currentLayout = "mosaic"
    this.streamsSwapped = false
    this.availableQualityOptions = []
    this.selectedQualityKey = "auto"
    this.lastVolume = 1
    this.captionsEnabled = false
    this.videoControlsHideTimeout = null
    this.videoControlsFocusPinned = false
    this.volumePanelShowTimeout = null
    this.volumePanelHideTimeout = null
    this.volumePanelFloatingCleanup = null
    this.settingsMenuFloatingCleanup = null

    this._bindMediaEvents()
    this._resetFloatingPanels()
    this._initializePlayer()
    this._initMultiStream()
    this._startLoadWatchdog()

    this._syncSpeedState()
    this._syncPlayState()
    this._syncVolumeState()
    this._syncTimeState()
    this._syncFullscreenState()
    this._setupResponsiveControls()
  }

  disconnect() {
    this._unbindMediaEvents()
    this._teardownStreamSync()
    this._endMosaicResize()
    this._clearLoadWatchdog()
    this._resetFloatingPanels()
    this._clearVideoControlsHideTimeout()
    this._teardownResponsiveControls()

    if (this.player) {
      try { this.player.destroy() } catch (_) { /* noop */ }
      this.player = null
    }
    if (this.hls) {
      try { this.hls.destroy() } catch (_) { /* noop */ }
      this.hls = null
    }
    this.secondaryHlsInstances.forEach((instance) => {
      try { instance.destroy() } catch (_) { /* noop */ }
    })
    this.secondaryHlsInstances = []
  }

  togglePlay(event) {
    event.preventDefault()

    if (this.mediaElement.paused) {
      this.mediaElement.play().catch(() => {})
    } else {
      this.mediaElement.pause()
    }
  }

  seekToPercentage(event) {
    const duration = this.mediaElement.duration || 0
    if (!duration) return

    const percent = Math.max(0, Math.min(100, Number(event.target.value)))
    this.mediaElement.currentTime = (percent / 100) * duration
    this._syncTimeState()
  }

  skip(event) {
    event.preventDefault()
    if (!this.hasMediaTarget) return

    const delta = Number(event.currentTarget.dataset.seconds)
    if (!Number.isFinite(delta)) return

    const duration = this.mediaElement.duration || 0
    const upperBound = duration > 0 ? duration : Infinity
    this.mediaElement.currentTime = Math.min(Math.max(0, this.mediaElement.currentTime + delta), upperBound)
    this._syncTimeState()
  }

  showSpeedSettings(event) {
    event.preventDefault()
    event.stopPropagation()

    this._showSettingsSubmenu("speed")
  }

  selectSpeed(event) {
    event.preventDefault()
    event.stopPropagation()

    const optionButton = event.target.closest("[data-speed-rate]")
    if (!optionButton || !this.hasMediaTarget) return

    const rate = Number(optionButton.dataset.speedRate)
    if (!Number.isFinite(rate)) return

    this.mediaElement.playbackRate = rate
  }

  toggleVolumePanel(event) {
    event.preventDefault()
    event.stopPropagation()

    if (!this.hasVolumePanelTarget) return

    this.volumePanelTarget.hidden ? this._showVolumePanel() : this._hideVolumePanel()
  }

  showVolumePanelOnHover(event) {
    if (this._isTouchPointer(event)) return

    this._scheduleShowVolumePanel()
  }

  showVolumePanelOnFocus() {
    this._showVolumePanel()
  }

  scheduleHideVolumePanel(event) {
    if (event?.type === "pointerleave" && this._isTouchPointer(event)) return
    if (event?.type === "focusout" && this._volumeAreaContains(event.relatedTarget)) return

    this._scheduleHideVolumePanel()
  }

  showVideoControlsOnActivity(event) {
    if (!this.isVideo) return
    if (event?.type === "pointermove" && this._isTouchPointer(event)) return

    if (event?.type === "pointerdown") this.videoControlsFocusPinned = false

    this._showVideoControls()
    this._scheduleVideoControlsHide()
  }

  showVideoControlsOnFocus(event) {
    if (!this.isVideo) return

    this.videoControlsFocusPinned = this._videoControlsFocusShouldPin(event?.target)
    this._showVideoControls()

    if (this.videoControlsFocusPinned) {
      this._clearVideoControlsHideTimeout()
    } else {
      this._scheduleVideoControlsHide()
    }
  }

  showVideoControlsOnKeyboard() {
    if (!this.isVideo) return

    this.videoControlsFocusPinned = true
    this._showVideoControls()
    this._clearVideoControlsHideTimeout()
  }

  scheduleHideVideoControls(event) {
    if (!this.isVideo) return
    if (event?.type === "pointerleave" && this._isTouchPointer(event)) return
    if (event?.type === "focusout" && this.element.contains(event.relatedTarget)) return

    if (event?.type === "focusout") this.videoControlsFocusPinned = false

    this._scheduleVideoControlsHide()
  }

  toggleMute(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.mediaElement.muted || this.mediaElement.volume === 0) {
      const restoredVolume = this.lastVolume > 0 ? this.lastVolume : 1
      this.mediaElement.muted = false
      this.mediaElement.volume = restoredVolume
    } else {
      this.lastVolume = this.mediaElement.volume > 0 ? this.mediaElement.volume : this.lastVolume
      this.mediaElement.muted = true
    }

    this._syncVolumeState()
  }

  setVolume(event) {
    const percent = Math.max(0, Math.min(100, Number(event.target.value)))
    const volume = percent / 100

    this.mediaElement.volume = volume
    this.mediaElement.muted = volume === 0
    if (volume > 0) this.lastVolume = volume

    this._syncVolumeState()
  }

  toggleSettingsMenu(event) {
    event.preventDefault()
    event.stopPropagation()

    if (!this.hasSettingsMenuTarget) return

    const shouldOpen = this.settingsMenuTarget.hidden
    this._hideVolumePanel({ scheduleVideoControlsHide: false })
    if (shouldOpen) {
      this._showSettingsRoot()
      this._showVideoControls()
      this._showFloatingPanel(
        this.settingsButtonTarget,
        this.settingsMenuTarget,
        this._floatingPanelPlacement("settings"),
        "settingsMenuFloatingCleanup"
      )
      this._scheduleVideoControlsHide()
    } else {
      this._hideSettingsMenu()
    }

    if (this.hasSettingsButtonTarget) {
      this.settingsButtonTarget.classList.toggle("is-active", shouldOpen)
      this.settingsButtonTarget.setAttribute("aria-expanded", String(shouldOpen))
    }
  }

  showSettingsRoot(event) {
    event?.preventDefault()
    event?.stopPropagation()

    this._showSettingsRoot()
  }

  showQualitySettings(event) {
    event.preventDefault()
    event.stopPropagation()

    if (this.availableQualityOptions.length === 0) return

    this._showSettingsSubmenu("quality")
  }

  selectQuality(event) {
    event.preventDefault()
    event.stopPropagation()

    const optionButton = event.target.closest("[data-quality-key]")
    if (!optionButton || !this.hls) return

    this._switchHlsQuality(optionButton.dataset.qualityKey)
  }

  toggleCaptions(event) {
    event.preventDefault()

    const tracks = this._textTracks()
    if (tracks.length === 0) return

    this.captionsEnabled = !this.captionsEnabled
    tracks.forEach((track) => {
      track.mode = this.captionsEnabled ? "showing" : "disabled"
    })

    this._syncCaptionsState()
  }

  toggleFullscreen(event) {
    event.preventDefault()
    if (!this.isVideo) return

    // A multi-stream mosaic must fullscreen the whole player element so every
    // stream is shown; Plyr's fullscreen would only expand the presenter video.
    if (!this.isMultiStream && this.player?.fullscreen) {
      this.player.fullscreen.toggle()
      return
    }

    if (document.fullscreenElement && this.element.contains(document.fullscreenElement)) {
      document.exitFullscreen?.()
    } else {
      this.element.requestFullscreen?.()
    }
  }

  handleWindowClick(event) {
    if (this.hasSettingsMenuTarget && !this.settingsMenuTarget.hidden) {
      const clickedInsideSettingsContainer = this.hasSettingsContainerTarget &&
        this.settingsContainerTarget.contains(event.target)
      const clickedInsideSettingsMenu = this.settingsMenuTarget.contains(event.target)
      const clickedInsideSettings = clickedInsideSettingsContainer || clickedInsideSettingsMenu
      if (!clickedInsideSettings) this._hideSettingsMenu()
    }

    if (this.hasVolumePanelTarget && !this.volumePanelTarget.hidden) {
      const clickedInsideVolumePanel = this.volumePanelTarget.contains(event.target)
      const clickedVolumeButton = this.hasVolumeButtonTarget && this.volumeButtonTarget.contains(event.target)
      if (!clickedInsideVolumePanel && !clickedVolumeButton) this._hideVolumePanel()
    }
  }

  handleEscape() {
    this._hideSettingsMenu()
    this._hideVolumePanel()
  }

  get mediaElement() {
    return this.mediaTarget
  }

  _bindMediaEvents() {
    this._onCanPlay = () => {
      this._markReady()
      this._syncCaptionsAvailability()
    }
    this._onMediaError = () => this._markError()
    this._onLoadedMetadata = () => {
      this._syncTimeState()
      this._syncCaptionsAvailability()
    }
    this._onDurationChange = () => this._syncTimeState()
    this._onTimeUpdate = () => this._syncTimeState()
    this._onPlay = () => this._syncPlayState()
    this._onPause = () => this._syncPlayState()
    this._onEnded = () => this._syncPlayState()
    this._onVolumeChange = () => this._syncVolumeState()
    this._onRateChange = () => this._syncSpeedState()
    this._onFullscreenChange = () => this._syncFullscreenState()

    this.mediaElement.addEventListener("canplay", this._onCanPlay)
    this.mediaElement.addEventListener("loadeddata", this._onCanPlay)
    this.mediaElement.addEventListener("error", this._onMediaError)
    this.mediaElement.addEventListener("loadedmetadata", this._onLoadedMetadata)
    this.mediaElement.addEventListener("durationchange", this._onDurationChange)
    this.mediaElement.addEventListener("timeupdate", this._onTimeUpdate)
    this.mediaElement.addEventListener("play", this._onPlay)
    this.mediaElement.addEventListener("pause", this._onPause)
    this.mediaElement.addEventListener("ended", this._onEnded)
    this.mediaElement.addEventListener("volumechange", this._onVolumeChange)
    this.mediaElement.addEventListener("ratechange", this._onRateChange)
    document.addEventListener("fullscreenchange", this._onFullscreenChange)

    this._textTrackList = this.mediaElement.textTracks
    if (this._textTrackList?.addEventListener) {
      this._onTextTrackChange = () => this._syncCaptionsAvailability()
      this._textTrackList.addEventListener("addtrack", this._onTextTrackChange)
      this._textTrackList.addEventListener("removetrack", this._onTextTrackChange)
      this._textTrackList.addEventListener("change", this._onTextTrackChange)
    }
  }

  _unbindMediaEvents() {
    if (!this.hasMediaTarget) return

    this.mediaElement.removeEventListener("canplay", this._onCanPlay)
    this.mediaElement.removeEventListener("loadeddata", this._onCanPlay)
    this.mediaElement.removeEventListener("error", this._onMediaError)
    this.mediaElement.removeEventListener("loadedmetadata", this._onLoadedMetadata)
    this.mediaElement.removeEventListener("durationchange", this._onDurationChange)
    this.mediaElement.removeEventListener("timeupdate", this._onTimeUpdate)
    this.mediaElement.removeEventListener("play", this._onPlay)
    this.mediaElement.removeEventListener("pause", this._onPause)
    this.mediaElement.removeEventListener("ended", this._onEnded)
    this.mediaElement.removeEventListener("volumechange", this._onVolumeChange)
    this.mediaElement.removeEventListener("ratechange", this._onRateChange)
    document.removeEventListener("fullscreenchange", this._onFullscreenChange)

    if (this._textTrackList?.removeEventListener && this._onTextTrackChange) {
      this._textTrackList.removeEventListener("addtrack", this._onTextTrackChange)
      this._textTrackList.removeEventListener("removetrack", this._onTextTrackChange)
      this._textTrackList.removeEventListener("change", this._onTextTrackChange)
    }
  }

  _initializePlayer() {
    this._suppressNativeVideoControls()

    const useHlsJs = this.isHlsSource && Hls.isSupported()
    this._log("connect", {
      mediaKind: this.mediaKindValue,
      isHlsSource: this.isHlsSource,
      useHlsJs,
      src: this.srcValue
    })

    if (useHlsJs) {
      this._initWithHlsJs()
      return
    }

    if (this.srcValue) this.mediaElement.src = this.srcValue
    this.player = new Plyr(this.mediaElement, this._plyrOptions())
    this._afterPlayerInitialized()
  }

  _initWithHlsJs() {
    const hlsConfig = { debug: this.debugValue }
    if (this.useCredentialsValue) {
      hlsConfig.xhrSetup = (xhr) => { xhr.withCredentials = true }
    }
    this.hls = new Hls(hlsConfig)

    this.hls.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
      const levels  = data.levels || this.hls.levels || []
      this.availableQualityOptions = this._buildQualityOptionsFromLevels(levels)
      this.selectedQualityKey = "auto"
      this._renderQualityOptions()

      if (!this.player) {
        this.player = new Plyr(this.mediaElement, this._plyrOptions())
        this._afterPlayerInitialized()
      }
    })

    this.hls.on(Hls.Events.LEVEL_SWITCHED, (_event, data) => {
      this._syncSelectedQualityFromLevel(data.level)
    })

    this.hls.on(Hls.Events.ERROR, (_event, data) => {
      this._log("HLS error", data)
      if (data?.fatal) this._markError()
    })

    this.hls.loadSource(this.srcValue)
    this.hls.attachMedia(this.mediaElement)
  }

  // Boots every secondary stream (index > 0) alongside the presenter: each gets
  // its own hls.js instance (or a plain file src), stays muted with no controls,
  // and is slaved to the presenter's clock. The presenter (mediaTargets[0]) keeps
  // driving all of the custom controls exactly as in the single-stream path.
  _initMultiStream() {
    if (!this.isMultiStream) return

    this.secondaryStreams = this.mediaTargets.slice(1)

    this.secondaryStreams.forEach((media) => {
      media.muted = true
      media.controls = false
      media.removeAttribute("controls")

      const type = media.dataset.streamType
      const src = media.dataset.streamSrc || ""
      const useHlsJs = type === "hls" && src.length > 0 && Hls.isSupported()

      if (useHlsJs) {
        const config = {}
        if (media.dataset.streamUseCredentials === "true") {
          config.xhrSetup = (xhr) => { xhr.withCredentials = true }
        }
        const hls = new Hls(config)
        hls.on(Hls.Events.ERROR, (_event, data) => { if (data?.fatal) this._log("secondary HLS error", data) })
        hls.loadSource(src)
        hls.attachMedia(media)
        this.secondaryHlsInstances.push(hls)
      } else if (src) {
        media.src = src
      }
    })

    this._bindStreamSync()
    this._applyLayout(this.currentLayout)
    this._syncResizerAria()
  }

  // Mirrors the presenter's transport (play/pause/seek/rate) onto every secondary
  // stream and hard-snaps drift on each timeupdate, with the presenter acting as
  // the master clock. Buffering on the presenter pauses the followers so they
  // never run ahead while it re-buffers.
  _bindStreamSync() {
    this._onMasterPlay = () => this._playSecondaries()
    this._onMasterPause = () => this._pauseSecondaries()
    this._onMasterSeek = () => this._syncSecondaryTimes(true)
    this._onMasterRate = () => this._syncSecondaryRates()
    this._onMasterWaiting = () => this._pauseSecondaries()
    this._onMasterPlaying = () => { if (!this.mediaElement.paused) this._playSecondaries() }
    this._onMasterDrift = () => this._syncSecondaryTimes(false)

    this.mediaElement.addEventListener("play", this._onMasterPlay)
    this.mediaElement.addEventListener("pause", this._onMasterPause)
    this.mediaElement.addEventListener("seeking", this._onMasterSeek)
    this.mediaElement.addEventListener("seeked", this._onMasterSeek)
    this.mediaElement.addEventListener("ratechange", this._onMasterRate)
    this.mediaElement.addEventListener("waiting", this._onMasterWaiting)
    this.mediaElement.addEventListener("playing", this._onMasterPlaying)
    this.mediaElement.addEventListener("timeupdate", this._onMasterDrift)

    this._syncSecondaryRates()
  }

  _teardownStreamSync() {
    if (!this.isMultiStream || !this._onMasterPlay) return

    this.mediaElement.removeEventListener("play", this._onMasterPlay)
    this.mediaElement.removeEventListener("pause", this._onMasterPause)
    this.mediaElement.removeEventListener("seeking", this._onMasterSeek)
    this.mediaElement.removeEventListener("seeked", this._onMasterSeek)
    this.mediaElement.removeEventListener("ratechange", this._onMasterRate)
    this.mediaElement.removeEventListener("waiting", this._onMasterWaiting)
    this.mediaElement.removeEventListener("playing", this._onMasterPlaying)
    this.mediaElement.removeEventListener("timeupdate", this._onMasterDrift)
  }

  _playSecondaries() {
    this.secondaryStreams.forEach((media) => {
      this._alignSecondary(media, true)
      media.play().catch(() => {})
    })
  }

  _pauseSecondaries() {
    this.secondaryStreams.forEach((media) => media.pause())
  }

  _syncSecondaryRates() {
    const rate = this.mediaElement.playbackRate || 1
    this.secondaryStreams.forEach((media) => { media.playbackRate = rate })
  }

  // Re-aligns each follower to the presenter's time. When `force` is true (a seek)
  // every follower is snapped; otherwise (periodic timeupdate) only those that
  // have drifted past the threshold are corrected, avoiding constant reseeking.
  _syncSecondaryTimes(force) {
    this.secondaryStreams.forEach((media) => this._alignSecondary(media, force))
  }

  _alignSecondary(media, force) {
    const master = this.mediaElement
    if (!Number.isFinite(master.currentTime)) return

    if (force || Math.abs(media.currentTime - master.currentTime) > STREAM_SYNC_DRIFT_SECONDS) {
      try { media.currentTime = master.currentTime } catch (_) { /* not seekable yet */ }
    }
  }

  showViewSettings(event) {
    event.preventDefault()
    event.stopPropagation()

    this._showSettingsSubmenu("view")
  }

  selectLayout(event) {
    event.preventDefault()
    event.stopPropagation()

    const optionButton = event.target.closest("[data-layout-key]")
    if (!optionButton) return

    this._applyLayout(optionButton.dataset.layoutKey)
  }

  swapStreams(event) {
    event?.preventDefault()
    event?.stopPropagation()

    this.streamsSwapped = !this.streamsSwapped
    this.element.classList.toggle("is-swapped", this.streamsSwapped)
  }

  _applyLayout(layoutKey) {
    const layout = STREAM_LAYOUT_LABELS[layoutKey] ? layoutKey : "mosaic"
    this.currentLayout = layout
    this.element.dataset.layout = layout
    if (this.hasMosaicTarget) this.mosaicTarget.dataset.layout = layout

    if (this.hasViewValueTarget) this.viewValueTarget.textContent = STREAM_LAYOUT_LABELS[layout]

    if (this.hasViewOptionsTarget) {
      this.viewOptionsTarget.querySelectorAll("[data-layout-key]").forEach((button) => {
        const isActive = button.dataset.layoutKey === layout
        button.classList.toggle("is-active", isActive)
        button.setAttribute("aria-checked", String(isActive))
      })
    }
  }

  // --- Resizable side-by-side divider -------------------------------------
  // The divider drives the mosaic's `--mosaic-columns` grid template. The ratio
  // is positional (percent width of the on-screen left pane), so it tracks the
  // pointer directly regardless of which stream is swapped into which column.

  startMosaicResize(event) {
    if (this.currentLayout !== "mosaic" || !this.hasMosaicTarget) return
    event.preventDefault()

    this._resizePointerId = event.pointerId
    try { event.currentTarget.setPointerCapture(event.pointerId) } catch (_) { /* noop */ }

    this.element.classList.add("is-resizing")

    this._onResizeMove = (moveEvent) => this._onMosaicResizeMove(moveEvent)
    this._onResizeEnd = () => this._endMosaicResize()
    window.addEventListener("pointermove", this._onResizeMove)
    window.addEventListener("pointerup", this._onResizeEnd)
    window.addEventListener("pointercancel", this._onResizeEnd)

    this._onMosaicResizeMove(event)
  }

  _onMosaicResizeMove(event) {
    if (!this.hasMosaicTarget) return

    const rect = this.mosaicTarget.getBoundingClientRect()
    if (rect.width === 0) return

    this._setMosaicSplit(((event.clientX - rect.left) / rect.width) * 100)
  }

  _endMosaicResize() {
    this.element.classList.remove("is-resizing")

    if (this._onResizeMove) window.removeEventListener("pointermove", this._onResizeMove)
    if (this._onResizeEnd) {
      window.removeEventListener("pointerup", this._onResizeEnd)
      window.removeEventListener("pointercancel", this._onResizeEnd)
    }
    this._onResizeMove = null
    this._onResizeEnd = null

    if (this._resizePointerId != null && this.hasResizerTarget) {
      try { this.resizerTarget.releasePointerCapture(this._resizePointerId) } catch (_) { /* noop */ }
    }
    this._resizePointerId = null
  }

  resetMosaicSplit(event) {
    event?.preventDefault()

    this.mosaicRatio = null
    if (this.hasMosaicTarget) this.mosaicTarget.style.removeProperty("--mosaic-columns")
    this._syncResizerAria()
  }

  nudgeMosaicSplit(event) {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return
    event.preventDefault()

    const current = this.mosaicRatio ?? 50
    const delta = event.key === "ArrowLeft" ? -MOSAIC_SPLIT_STEP : MOSAIC_SPLIT_STEP
    this._setMosaicSplit(current + delta)
  }

  _setMosaicSplit(ratio) {
    const clamped = Math.min(MOSAIC_SPLIT_MAX, Math.max(MOSAIC_SPLIT_MIN, ratio))
    this.mosaicRatio = clamped

    if (this.hasMosaicTarget) {
      this.mosaicTarget.style.setProperty("--mosaic-columns", `${clamped}fr 10px ${100 - clamped}fr`)
    }
    this._syncResizerAria()
  }

  _syncResizerAria() {
    if (!this.hasResizerTarget) return

    this.resizerTarget.setAttribute("aria-valuenow", String(Math.round(this.mosaicRatio ?? 50)))
    this.resizerTarget.setAttribute("aria-valuemin", String(MOSAIC_SPLIT_MIN))
    this.resizerTarget.setAttribute("aria-valuemax", String(MOSAIC_SPLIT_MAX))
  }

  _switchHlsQuality(qualityKey) {
    if (!this.hls) return

    if (qualityKey === "auto") {
      this.selectedQualityKey = "auto"
      this.hls.currentLevel = -1
      this._renderQualityOptions()
      return
    }

    const option = this.availableQualityOptions.find((entry) => entry.key === qualityKey)
    if (!option) return

    let levelIndex = -1
    if (this.isVideo && option.height) {
      levelIndex = this.hls.levels.findIndex((level) => level.height === option.height)
    } else if (!this.isVideo && option.bitrate) {
      levelIndex = this.hls.levels.findIndex((level) => level.bitrate === option.bitrate)
    }

    if (levelIndex < 0) return

    this.selectedQualityKey = option.key
    this.hls.currentLevel = levelIndex
    this._renderQualityOptions()
  }

  _plyrOptions() {
    return {
      controls: [],
      settings: [],
      autoplay: false,
      keyboard: { focused: true, global: false },
      tooltips: { controls: false, seek: false },
      captions: { active: false, update: true }
    }
  }

  _buildQualityOptionsFromLevels(levels) {
    if (!levels || levels.length < 2) return []

    if (this.isVideo) {
      const heights = [...new Set(
        levels
          .map((level) => level.height)
          .filter((height) => Number.isFinite(height) && height > 0)
      )].sort((a, b) => a - b)

      if (heights.length < 2) return []

      return [
        { key: "auto", label: "Auto" },
        ...heights.map((height) => ({
          key: `h-${height}`,
          label: `${height}p`,
          height
        }))
      ]
    }

    const bitrates = [...new Set(
      levels
        .map((level) => level.bitrate)
        .filter((bitrate) => Number.isFinite(bitrate) && bitrate > 0)
    )].sort((a, b) => a - b)

    if (bitrates.length < 2) return []

    return [
      { key: "auto", label: "Auto" },
      ...bitrates.map((bitrate) => ({
        key: `b-${bitrate}`,
        label: `${Math.round(bitrate / 1000)} kbps`,
        bitrate
      }))
    ]
  }

  _syncSelectedQualityFromLevel(levelIndex) {
    if (!this.hls || this.availableQualityOptions.length === 0) return

    if (this.hls.autoLevelEnabled || levelIndex < 0) {
      this.selectedQualityKey = "auto"
      this._renderQualityOptions()
      return
    }

    const level = this.hls.levels[levelIndex]
    if (!level) return

    let match = null
    if (this.isVideo && level.height) {
      match = this.availableQualityOptions.find((entry) => entry.height === level.height)
    } else if (!this.isVideo && level.bitrate) {
      match = this.availableQualityOptions.find((entry) => entry.bitrate === level.bitrate)
    }

    if (!match) return

    this.selectedQualityKey = match.key
    this._renderQualityOptions()
  }

  _renderQualityOptions() {
    if (!this.hasQualityOptionsTarget) return

    const hasSelectableQuality = this.availableQualityOptions.length > 0
    if (this.hasQualityMenuButtonTarget) this.qualityMenuButtonTarget.hidden = !hasSelectableQuality
    if (!hasSelectableQuality && this.hasQualitySubmenuTarget && !this.qualitySubmenuTarget.hidden) {
      this._showSettingsRoot()
    }
    this.qualityOptionsTarget.innerHTML = ""

    if (!hasSelectableQuality) return

    if (this.hasQualityValueTarget) {
      const selectedQuality = this.availableQualityOptions.find((entry) => entry.key === this.selectedQualityKey)
      this.qualityValueTarget.textContent = selectedQuality?.label || "Auto"
    }

    this.availableQualityOptions.forEach((option) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "app-media-player__settings-option"
      button.dataset.qualityKey = option.key
      button.setAttribute("role", "menuitemradio")

      const label = document.createElement("span")
      label.textContent = option.label

      const icon = document.createElement("i")
      icon.className = "ph ph-check"
      icon.setAttribute("aria-hidden", "true")

      button.append(label, icon)

      if (option.key === this.selectedQualityKey) {
        button.classList.add("is-active")
        button.setAttribute("aria-checked", "true")
      } else {
        button.setAttribute("aria-checked", "false")
      }

      this.qualityOptionsTarget.appendChild(button)
    })
  }

  _afterPlayerInitialized() {
    this._suppressNativeVideoControls()
    this._syncCaptionsAvailability()
    this._syncPlayState()
    this._syncTimeState()
    this._syncVolumeState()
    this._syncSpeedState()
    this._renderQualityOptions()
  }

  _suppressNativeVideoControls() {
    if (!this.isVideo) return

    this.mediaElement.controls = false
    this.mediaElement.removeAttribute("controls")
  }

  _syncPlayState() {
    const isPlaying = !this.mediaElement.paused && !this.mediaElement.ended

    if (this.isVideo) {
      this.element.classList.toggle("is-playing", isPlaying)
      this._syncVideoControlsVisibility(isPlaying)
    } else {
      this.element.classList.remove("is-playing")
      this.element.classList.remove("is-controls-active")
    }

    if (this.hasPlayIconTarget) this.playIconTarget.classList.toggle("is-hidden", isPlaying)
    if (this.hasPauseIconTarget) this.pauseIconTarget.classList.toggle("is-hidden", !isPlaying)

    if (this.hasPlayButtonTarget) {
      this.playButtonTarget.setAttribute("aria-label", isPlaying ? "Pause" : "Play")
      this.playButtonTarget.setAttribute("aria-pressed", String(isPlaying))
    }
  }

  _syncTimeState() {
    const current = this.mediaElement.currentTime || 0
    const duration = this.mediaElement.duration || 0

    if (this.hasCurrentTimeTarget) this.currentTimeTarget.textContent = this._formatTime(current)
    if (this.hasDurationTarget) this.durationTarget.textContent = this._formatTime(duration)

    if (this.hasSeekRangeTarget) {
      const percent = duration > 0 ? (current / duration) * 100 : 0
      this.seekRangeTarget.value = percent
      this.seekRangeTarget.style.setProperty("--seek-fill", `${percent}%`)
    }
  }

  _syncVolumeState() {
    const isMuted = this.mediaElement.muted
    const volume = isMuted ? 0 : this.mediaElement.volume
    const percent = Math.round(volume * 100)

    if (!isMuted && volume > 0) this.lastVolume = volume

    // The volume control is rendered twice on audio players — the toolbar popup
    // (wide screens) and the settings-menu row (compact screens) — so every copy
    // is kept in sync rather than just the first target.
    this.volumeRangeTargets.forEach((range) => {
      range.value = percent
      range.style.setProperty("--volume-fill", `${percent}%`)
    })

    this.volumeIconTargets.forEach((icon) => {
      icon.classList.remove("ph-speaker-x", "ph-speaker-low", "ph-speaker-high")
      if (percent === 0) {
        icon.classList.add("ph-speaker-x")
      } else if (percent < 50) {
        icon.classList.add("ph-speaker-low")
      } else {
        icon.classList.add("ph-speaker-high")
      }
    })

    const isSilent = percent === 0
    this.volumeButtonTargets.forEach((button) => {
      button.setAttribute("aria-label", isSilent ? "Unmute" : "Mute")
      button.setAttribute("aria-pressed", String(isSilent))
    })
  }

  _syncSpeedState() {
    const playbackRate = this.mediaElement.playbackRate || 1
    if (this.hasSpeedValueTarget) this.speedValueTarget.textContent = this._formatSpeedLabel(playbackRate)

    if (!this.hasSpeedOptionsTarget) return

    this.speedOptionsTarget.querySelectorAll("[data-speed-rate]").forEach((button) => {
      const isActive = Math.abs(Number(button.dataset.speedRate) - playbackRate) < 0.001
      button.classList.toggle("is-active", isActive)
      button.setAttribute("aria-checked", String(isActive))
    })
  }

  _syncCaptionsAvailability() {
    if (!this.hasCaptionsButtonTarget) return

    const tracks = this._textTracks()
    const hasCaptions = tracks.length > 0

    this.captionsButtonTarget.hidden = !hasCaptions
    if (!hasCaptions) {
      this.captionsEnabled = false
      this._syncCaptionsState()
      return
    }

    this.captionsEnabled = tracks.some((track) => track.mode === "showing")
    this._syncCaptionsState()
  }

  _syncCaptionsState() {
    if (!this.hasCaptionsButtonTarget) return

    this.captionsButtonTarget.classList.toggle("is-active", this.captionsEnabled)
    this.captionsButtonTarget.setAttribute("aria-pressed", String(this.captionsEnabled))
  }

  _syncFullscreenState() {
    if (!this.hasFullscreenButtonTarget) return

    const inBrowserFullscreen = Boolean(document.fullscreenElement) &&
      this.element.contains(document.fullscreenElement)
    const isFullscreen = this.player?.fullscreen?.active || inBrowserFullscreen

    this.fullscreenButtonTarget.classList.toggle("is-active", isFullscreen)
    this.fullscreenButtonTarget.setAttribute("aria-pressed", String(isFullscreen))
    this.fullscreenButtonTarget.setAttribute("aria-label", isFullscreen ? "Exit fullscreen" : "Enter fullscreen")

    if (this.hasFullscreenIconTarget) {
      this.fullscreenIconTarget.classList.remove("ph-corners-out", "ph-corners-in")
      this.fullscreenIconTarget.classList.add(isFullscreen ? "ph-corners-in" : "ph-corners-out")
    }
  }

  _textTracks() {
    return Array.from(this.mediaElement.textTracks || []).filter((track) => {
      return track.kind === "subtitles" || track.kind === "captions"
    })
  }

  _syncVideoControlsVisibility(isPlaying) {
    this._clearVideoControlsHideTimeout()

    if (isPlaying) {
      this._showVideoControls()
      this._scheduleVideoControlsHide()
    } else {
      this.videoControlsFocusPinned = false
      this._showVideoControls()
    }
  }

  _showVideoControls() {
    if (!this.isVideo) return

    this.element.classList.add("is-controls-active")
  }

  _hideVideoControls() {
    if (!this._videoControlsShouldAutoHide() || this._videoControlsArePinned()) return

    this._hideSettingsMenu({ scheduleVideoControlsHide: false })
    this._hideVolumePanel({ scheduleVideoControlsHide: false })
    this.element.classList.remove("is-controls-active")
  }

  _scheduleVideoControlsHide() {
    this._clearVideoControlsHideTimeout()

    if (!this._videoControlsShouldAutoHide() || this._videoControlsArePinned()) return

    this.videoControlsHideTimeout = window.setTimeout(() => {
      this.videoControlsHideTimeout = null
      this._hideVideoControls()
    }, this.videoControlsHideDelayValue)
  }

  _clearVideoControlsHideTimeout() {
    if (!this.videoControlsHideTimeout) return

    window.clearTimeout(this.videoControlsHideTimeout)
    this.videoControlsHideTimeout = null
  }

  _videoControlsShouldAutoHide() {
    return this.isVideo && !this.mediaElement.paused && !this.mediaElement.ended
  }

  _videoControlsArePinned() {
    return this.videoControlsFocusPinned
  }

  _videoControlsFocusShouldPin(target) {
    return Boolean(target?.matches?.(":focus-visible"))
  }

  _showVolumePanel() {
    if (!this.hasVolumePanelTarget || !this.hasVolumeButtonTarget) return

    this._clearVolumePanelTimers()
    this._hideSettingsMenu({ scheduleVideoControlsHide: false })
    this._showVideoControls()
    this._showFloatingPanel(
      this.volumeButtonTarget,
      this.volumePanelTarget,
      this._floatingPanelPlacement("volume"),
      "volumePanelFloatingCleanup"
    )
    this.volumeButtonTarget.classList.add("is-active")
    this.volumeButtonTarget.setAttribute("aria-expanded", "true")
    this._scheduleVideoControlsHide()
  }

  _scheduleShowVolumePanel() {
    this._clearVolumePanelHideTimeout()
    if (!this.hasVolumePanelTarget || !this.volumePanelTarget.hidden) return

    this._clearVolumePanelShowTimeout()
    this.volumePanelShowTimeout = window.setTimeout(() => {
      this.volumePanelShowTimeout = null
      this._showVolumePanel()
    }, 80)
  }

  _scheduleHideVolumePanel() {
    this._clearVolumePanelShowTimeout()
    this._clearVolumePanelHideTimeout()

    this.volumePanelHideTimeout = window.setTimeout(() => {
      this.volumePanelHideTimeout = null
      if (this._volumeAreaIsActive()) return

      this._hideVolumePanel()
    }, 200)
  }

  _volumeAreaIsActive() {
    const activeElement = document.activeElement
    const hoverTargets = [
      this.hasVolumeContainerTarget ? this.volumeContainerTarget : null,
      this.hasVolumeButtonTarget ? this.volumeButtonTarget : null,
      this.hasVolumePanelTarget ? this.volumePanelTarget : null
    ].filter(Boolean)

    return this._volumeAreaContains(activeElement) || hoverTargets.some((target) => target.matches(":hover"))
  }

  _volumeAreaContains(target) {
    if (!target) return false

    return (this.hasVolumeContainerTarget && this.volumeContainerTarget.contains(target)) ||
      (this.hasVolumeButtonTarget && this.volumeButtonTarget.contains(target)) ||
      (this.hasVolumePanelTarget && this.volumePanelTarget.contains(target))
  }

  _clearVolumePanelTimers() {
    this._clearVolumePanelShowTimeout()
    this._clearVolumePanelHideTimeout()
  }

  _clearVolumePanelShowTimeout() {
    if (!this.volumePanelShowTimeout) return

    window.clearTimeout(this.volumePanelShowTimeout)
    this.volumePanelShowTimeout = null
  }

  _clearVolumePanelHideTimeout() {
    if (!this.volumePanelHideTimeout) return

    window.clearTimeout(this.volumePanelHideTimeout)
    this.volumePanelHideTimeout = null
  }

  _isTouchPointer(event) {
    return event?.pointerType === "touch"
  }

  _hideVolumePanel({ scheduleVideoControlsHide = true } = {}) {
    if (!this.hasVolumePanelTarget) return

    this._clearVolumePanelTimers()
    this._hideFloatingPanel(this.volumePanelTarget, "volumePanelFloatingCleanup")
    if (this.hasVolumeButtonTarget) {
      this.volumeButtonTarget.classList.remove("is-active")
      this.volumeButtonTarget.setAttribute("aria-expanded", "false")
    }
    if (!scheduleVideoControlsHide) return

    this._scheduleVideoControlsHide()
  }

  _hideSettingsMenu({ scheduleVideoControlsHide = true } = {}) {
    if (!this.hasSettingsMenuTarget) return

    this._hideFloatingPanel(this.settingsMenuTarget, "settingsMenuFloatingCleanup")
    this._showSettingsRoot()
    if (this.hasSettingsButtonTarget) {
      this.settingsButtonTarget.classList.remove("is-active")
      this.settingsButtonTarget.setAttribute("aria-expanded", "false")
    }
    if (!scheduleVideoControlsHide) return

    this._scheduleVideoControlsHide()
  }

  _showSettingsRoot() {
    if (this.hasSettingsRootTarget) this.settingsRootTarget.hidden = false
    if (this.hasSpeedSubmenuTarget) this.speedSubmenuTarget.hidden = true
    if (this.hasQualitySubmenuTarget) this.qualitySubmenuTarget.hidden = true
    if (this.hasViewSubmenuTarget) this.viewSubmenuTarget.hidden = true
  }

  _showSettingsSubmenu(submenu) {
    if (this.hasSettingsRootTarget) this.settingsRootTarget.hidden = true
    if (this.hasSpeedSubmenuTarget) this.speedSubmenuTarget.hidden = submenu !== "speed"
    if (this.hasQualitySubmenuTarget) this.qualitySubmenuTarget.hidden = submenu !== "quality"
    if (this.hasViewSubmenuTarget) this.viewSubmenuTarget.hidden = submenu !== "view"
  }

  _showFloatingPanel(reference, panel, placement, cleanupName) {
    if (!reference || !panel) return

    this._cleanupFloatingPanel(cleanupName)
    this._portalFloatingPanel(panel, cleanupName)
    panel.hidden = false
    panel.classList.add("is-floating")

    const updatePosition = () => {
      const boundaryPadding = this._floatingPanelBoundaryPadding()

      computePosition(reference, panel, {
        placement,
        strategy: "fixed",
        middleware: [
          offset(10),
          flip({
            crossAxis: false,
            fallbackPlacements: [this._floatingPanelFallbackPlacement(placement)],
            padding: boundaryPadding
          }),
          shift({ padding: boundaryPadding })
        ]
      }).then(({ x, y }) => {
        if (panel.hidden) return

        Object.assign(panel.style, {
          left: `${x}px`,
          top: `${y}px`
        })
      })
    }

    updatePosition()
    this[cleanupName] = autoUpdate(reference, panel, updatePosition)
  }

  _hideFloatingPanel(panel, cleanupName) {
    this._cleanupFloatingPanel(cleanupName)

    panel.hidden = true
    panel.classList.remove("is-floating")
    panel.style.left = ""
    panel.style.top = ""
    this._restoreFloatingPanel(panel, cleanupName)
  }

  _portalFloatingPanel(panel, cleanupName) {
    if (!this.isVideo || panel.parentElement === this.element) return

    const portalName = this._floatingPanelPortalName(cleanupName)
    if (this[portalName]) return

    this[portalName] = {
      parent: panel.parentNode,
      nextSibling: panel.nextSibling
    }
    this.element.appendChild(panel)
  }

  _restoreFloatingPanel(panel, cleanupName) {
    const portalName = this._floatingPanelPortalName(cleanupName)
    const portal = this[portalName]
    if (!portal) return

    if (portal.parent?.isConnected && portal.nextSibling?.parentNode === portal.parent) {
      portal.parent.insertBefore(panel, portal.nextSibling)
    } else if (portal.parent?.isConnected) {
      portal.parent.appendChild(panel)
    }

    this[portalName] = null
  }

  _floatingPanelPortalName(cleanupName) {
    return `${cleanupName}Portal`
  }

  _cleanupFloatingPanel(cleanupName) {
    if (!this[cleanupName]) return

    this[cleanupName]()
    this[cleanupName] = null
  }

  _floatingPanelPlacement(panelKind) {
    const alignment = panelKind === "settings" ? "end" : "start"

    return `top-${alignment}`
  }

  _floatingPanelFallbackPlacement(placement) {
    return placement.startsWith("top") ? placement.replace("top", "bottom") : placement.replace("bottom", "top")
  }

  _floatingPanelBoundaryPadding() {
    const padding = { top: 12, right: 12, bottom: 12, left: 12 }
    const topbar = document.querySelector(".topbar")
    if (!topbar) return padding

    const topbarStyle = window.getComputedStyle(topbar)
    const topbarRect = topbar.getBoundingClientRect()
    const topbarOccupiesViewportTop = ["fixed", "sticky"].includes(topbarStyle.position) &&
      topbarRect.top <= 0 &&
      topbarRect.bottom > 0

    if (topbarOccupiesViewportTop) padding.top = Math.ceil(topbarRect.bottom) + 12

    return padding
  }

  _resetFloatingPanels() {
    this._hideVolumePanel()
    this._hideSettingsMenu()
  }

  // Control bars adapt to the player's own width (not the viewport) so the toolbar
  // stays on a single row inside narrow containers like split views and drawers.
  // As space runs out the controls fold away in stages — skip + download first,
  // then volume, then the seek bar — via the cumulative `is-compact-1..3` flags.
  // The fold level is recomputed from scratch on every resize, so it is a pure
  // function of width with no hysteresis needed.
  _setupResponsiveControls() {
    if (typeof ResizeObserver === "undefined") return

    this._resizeObserver = new ResizeObserver(() => this._syncCompactLayout())
    this._resizeObserver.observe(this.element)
    this._syncCompactLayout()
  }

  _teardownResponsiveControls() {
    if (!this._resizeObserver) return

    this._resizeObserver.disconnect()
    this._resizeObserver = null
  }

  _syncCompactLayout() {
    if (this.element.clientWidth === 0) return

    // Start from the full layout and fold one stage at a time until the toolbar
    // stops wrapping (or there is nothing left to fold). These DOM reads/writes
    // are synchronous, so no intermediate stage is ever painted.
    let level = 0
    this._applyCompactLevel(level)
    while (level < MAX_COMPACT_LEVEL && this._controlsWrapped()) {
      level += 1
      this._applyCompactLevel(level)
    }
  }

  _applyCompactLevel(level) {
    this.element.classList.toggle("is-compact-1", level >= 1)
    this.element.classList.toggle("is-compact-2", level >= 2)
    this.element.classList.toggle("is-compact-3", level >= 3)
  }

  _controlsWrapped() {
    const leftGroup = this.element.querySelector(".app-media-player__control-group--left")
    const rightGroup = this.element.querySelector(".app-media-player__control-group--right")
    if (!leftGroup || !rightGroup) return false

    return rightGroup.offsetTop > leftGroup.offsetTop + 1
  }

  _markReady() {
    this._clearLoadWatchdog()
    this.element.classList.remove("is-loading")
    this.element.classList.remove("is-error")
    this._hideError()
  }

  _markError(message) {
    this._clearLoadWatchdog()
    this.element.classList.remove("is-loading")
    this.element.classList.add("is-error")
    this._showError(message)
  }

  // Fails over from an infinite spinner to a clear, actionable state if the
  // media never becomes playable (HLS not packaged yet, a stalled raw-file
  // fallback, a 404, etc.). Cleared as soon as the media is ready or errors.
  _startLoadWatchdog() {
    this._clearLoadWatchdog()
    if (this.loadTimeoutValue <= 0) return
    if (this.mediaElement.readyState >= 2) return

    this.loadWatchdog = window.setTimeout(() => {
      this.loadWatchdog = null
      if (this.mediaElement.readyState >= 2) return

      this._markError("This video isn't ready to stream yet — it may still be processing. Retry in a moment.")
    }, this.loadTimeoutValue)
  }

  _clearLoadWatchdog() {
    if (!this.loadWatchdog) return

    window.clearTimeout(this.loadWatchdog)
    this.loadWatchdog = null
  }

  _showError(message) {
    if (this.hasErrorTextTarget && message) this.errorTextTarget.textContent = message
    if (this.hasErrorTarget) this.errorTarget.hidden = false
  }

  _hideError() {
    if (this.hasErrorTarget) this.errorTarget.hidden = true
  }

  // Rebuilds playback from scratch after a load failure: tears down the players
  // and re-initializes every stream, then restarts the watchdog. Works in every
  // surface (buyer, academy, admin) without a full page reload.
  retryLoad(event) {
    event?.preventDefault()

    this._clearLoadWatchdog()
    this._hideError()
    this.element.classList.remove("is-error")
    this.element.classList.add("is-loading")

    if (this.hls) {
      try { this.hls.destroy() } catch (_) { /* noop */ }
      this.hls = null
    }
    this.secondaryHlsInstances.forEach((instance) => {
      try { instance.destroy() } catch (_) { /* noop */ }
    })
    this.secondaryHlsInstances = []
    if (this.player) {
      try { this.player.destroy() } catch (_) { /* noop */ }
      this.player = null
    }

    this._initializePlayer()
    this._initMultiStream()

    this.mediaTargets.forEach((media) => { try { media.load() } catch (_) { /* noop */ } })

    this._startLoadWatchdog()
  }

  _formatTime(seconds) {
    if (isNaN(seconds) || !isFinite(seconds)) return "0:00"

    const total = Math.floor(seconds)
    const hours = Math.floor(total / 3600)
    const minutes = Math.floor((total % 3600) / 60)
    const remainder = total % 60

    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, "0")}:${String(remainder).padStart(2, "0")}`
    }

    return `${minutes}:${String(remainder).padStart(2, "0")}`
  }

  _log(...args) {
    if (!this.debugValue) return
    // eslint-disable-next-line no-console
    console.log("[media-player]", ...args)
  }

  _formatSpeedLabel(speed) {
    const normalizedSpeed = Number(speed)
    if (!Number.isFinite(normalizedSpeed)) return "Normal"
    if (normalizedSpeed === 1) return "Normal"

    return `${normalizedSpeed}x`
  }
}
